// The tasks list in sections (spec 19 § 3): Today holds what is due today or already late, This week the next six days,
// Later the rest — a task with no due date, and a finished one, goes where its date says, else Later. Sorting happens
// inside a section; the sections keep their order. The API filters by status, assignee, team and words, and Due this
// week and Show done (done tasks hidden until asked, R4) ask it too (`listQuery`), so a page is full of what is shown;
// the loaded pages are filtered here again, for what changed since they came.
import type { components } from '@orca/api-client'
import type { TaskFilters } from '../../shared/query/keys'
import { dueDays } from './DuePill'

type Task = components['schemas']['TaskDto']

export type DueSection = 'today' | 'week' | 'later'
export type TaskSort = 'due' | 'updated' | 'title' | 'assignee'
export const SECTIONS: readonly DueSection[] = ['today', 'week', 'later']
export const SORTS: readonly TaskSort[] = ['due', 'updated', 'title', 'assignee']

export function sectionOf(task: Task, now: Date = new Date()): DueSection {
  if (!task.dueOn) return 'later'
  const days = dueDays(task.dueOn, now)
  if (days <= 0) return 'today'
  return days <= 6 ? 'week' : 'later'
}

function compare(sort: TaskSort): (a: Task, b: Task) => number {
  if (sort === 'updated') return (a, b) => b.updatedAt.localeCompare(a.updatedAt)
  if (sort === 'title') return (a, b) => a.title.localeCompare(b.title)
  // By the assignee's name, the unassigned last, then by title.
  if (sort === 'assignee')
    return (a, b) =>
      (a.assignee?.displayName ?? '\uffff').localeCompare(b.assignee?.displayName ?? '\uffff') ||
      a.title.localeCompare(b.title)
  // Due date first (none last), then the newest.
  return (a, b) =>
    (a.dueOn ?? '9999-12-31').localeCompare(b.dueOn ?? '9999-12-31') || b.createdAt.localeCompare(a.createdAt)
}

/** The three sections, each sorted; an empty section stays in the result so the caller decides to draw it or not. */
export function groupByDue(tasks: readonly Task[], sort: TaskSort = 'due', now: Date = new Date()) {
  const groups: Record<DueSection, Task[]> = { today: [], week: [], later: [] }
  for (const task of tasks) groups[sectionOf(task, now)].push(task)
  return SECTIONS.map((section) => ({ section, tasks: groups[section].sort(compare(sort)) }))
}

/** What the list draws of the loaded tasks: done ones only with `showDone`, and only the next seven days with `week`. */
export function visibleTasks(
  tasks: readonly Task[],
  { showDone = false, week = false }: { showDone?: boolean; week?: boolean },
  now: Date = new Date(),
) {
  return tasks.filter((t) => (showDone || t.status !== 'done') && (!week || sectionOf(t, now) !== 'later'))
}

/** How many of these were finished on the viewer's own today. */
export function doneToday(tasks: readonly Task[], now: Date = new Date()) {
  return tasks.filter((t) => {
    if (!t.completedAt) return false
    const at = new Date(t.completedAt)
    return at.getFullYear() === now.getFullYear() && at.getMonth() === now.getMonth() && at.getDate() === now.getDate()
  }).length
}

const isoDay = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

/**
 * What the list asks the server for: its filters, the order (by due date unless the newest change first was picked;
 * title and assignee sort inside it), Due this week as the dated tasks before the eighth day from today, and Show done
 * off as `done=false` — except while searching, when the done matches are counted to offer Show done.
 */
export function listQuery(
  { week, ...filters }: TaskFilters & { week?: boolean },
  { sort = 'due', showDone = false }: { sort?: TaskSort; showDone?: boolean },
  now: Date = new Date(),
): TaskFilters {
  const query: TaskFilters = { ...filters, sort: sort === 'updated' ? 'updated' : 'due' }
  if (week) query.dueBefore = isoDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7))
  if (!showDone && filters.status !== 'done' && !filters.q) query.done = false
  return query
}
